import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetLoginInfo } from "@multiversx/sdk-dapp/hooks";
import { aiNexusApiService } from "../services/ai-nexus-api.service";
import { useRefreshAndSend } from "./useRefreshAndSend";
import { IDisplayInfo } from "@/types";
import { Transaction, Address } from "@multiversx/sdk-core";

interface PurchaseProductParams {
  productId: string;
  productName: string;
}

export const usePurchaseProduct = () => {
  const { tokenLogin } = useGetLoginInfo();
  const refreshAndSend = useRefreshAndSend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, productName }: PurchaseProductParams) => {
      if (!tokenLogin?.nativeAuthToken) {
        throw new Error("No wallet connected");
      }

      // Create transaction
      const transactionResponse = await aiNexusApiService.createTransaction({
        nativeToken: tokenLogin?.nativeAuthToken,
        productId,
      });

      // Properly construct MultiversX Transaction object
      const txData = transactionResponse.transactionData;
      const transaction = new Transaction({
        nonce: BigInt(txData.nonce),
        value: BigInt(txData.value),
        receiver: Address.newFromBech32(txData.receiver),
        sender: Address.newFromBech32(txData.sender),
        gasPrice: BigInt(txData.gasPrice),
        gasLimit: BigInt(txData.gasLimit),
        data: Buffer.from(txData.data, "base64"),
        chainID: txData.chainID,
        version: txData.version,
        options: txData.options,
      });

      // Prepare display info for transaction
      const displayInfo: IDisplayInfo = {
        processingMessage: `Processing purchase of ${productName}...`,
        errorMessage: `Failed to purchase ${productName}`,
        successMessage: `Successfully purchased ${productName}!`,
      };

      console.log("Transaction object:", transaction);

      // Send transaction for signing
      const result = await refreshAndSend([transaction], displayInfo);

      return result;
    },
    onSuccess: () => {
      // Invalidate relevant queries after successful purchase
      queryClient.invalidateQueries({ queryKey: ["wallet-verification"] });
      queryClient.invalidateQueries({ queryKey: ["account-tokens"] });
    },
    onError: (error) => {
      console.error("Purchase failed:", error);
    },
  });
};
